import {$qs, $qsAll, isMobile} from '../../@shared/ts/dom';
import {type WCPHPData} from '../../@type/woocommerce/php-data';
import {peachpay_hideLoadingSpinner, peachpay_initButton, shouldLoadButtonBasedOnAccountSettings, update_buttonIcon} from './button';
import {peachpayButtonTemplate, peachpayMiniButtonTemplate, peachpayMobileButtonTemplate} from './templates/button';

declare const peachpay_data: WCPHPData;

document.addEventListener('DOMContentLoaded', () => {
	if (!peachpay_isProductPage()) {
		return;
	}

	peachpay_placeButtonProductPage();
});

function peachpay_placeButtonProductPage() {
	if ((peachpay_isElementor() ?? peachpay_isAffiliateProduct()) || !peachpay_data.button_display_on_product_page) {
		return;
	}

	if (!shouldLoadButtonBasedOnAccountSettings()) {
		return;
	}

	if (isMobile() && peachpay_data.button_mobile_product_page === 'fixed') {
		document.body.insertAdjacentHTML('afterend', peachpayMobileButtonTemplate());
	} else {
		const position = peachpay_data.product_page_button_before_after || 'beforebegin';

		// Check for form element array to have at least one item, otherwise return null (such that the null coalescense below
		//  will continue checking other options).
		const isEligibleButtonPlacement = (candidateSpace: HTMLFormElement[]) => candidateSpace.length ? candidateSpace : null;

		const getAdjacentContainer = () => {
			if (position === 'beforebegin') {
				// Button Above
				return isEligibleButtonPlacement($qsAll('form.cart.varations_form_cart div.woocommerce-variation-add-to-cart')) // Variation products
					?? isEligibleButtonPlacement($qsAll('form.cart.grouped_form .single_add_to_cart_button')) // Grouped products
					?? isEligibleButtonPlacement($qsAll('form.cart.bundle_form div.bundle_wrap')) // Bundled products
					?? isEligibleButtonPlacement($qsAll('main form.cart div.quantity')) // Targets the main add to cart button
					?? isEligibleButtonPlacement($qsAll('form.cart div.quantity')) // Standard simple products with quantity
					?? $qsAll('form.cart'); // Default case
			}

			// Button Below
			return isEligibleButtonPlacement($qsAll('form.cart .single_add_to_cart_button')) // Beneath the add to cart button
					?? $qsAll('form.cart'); // Backup default case
		};

		const selector = peachpay_data.custom_button_placement.product_page;
		const $addToCartForm = selector ? $qsAll(selector) : getAdjacentContainer();
		const outOfStock = $qs('p.out-of-stock');

		if (!$addToCartForm || outOfStock) {
			return;
		}

		$addToCartForm.forEach($addToCartFormPart => {
			$addToCartFormPart.insertAdjacentHTML(position, peachpayButtonTemplate());
		});
	}

	// We must do this because we changed the <img> tags to <object> tags to
	// fix a Safari issue where it would not load animated SVGs. If we have
	// the hide class on the element right away, the SVG is not loaded, so the
	// first time you click the button it's not there. Only after it is visible
	// does it load, so we allow it to be visible for a split second before
	// hiding it here.
	peachpay_hideLoadingSpinner();

	const full = peachpay_data.button_alignment_product_page === 'full';
	// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
	const width = full ? '100%' : ((peachpay_data.button_width_product_page || '220') + 'px');
	peachpay_initButton({
		width,
		// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
		alignment: peachpay_data.button_alignment_product_page || 'left',
		borderRadius: peachpay_data.button_border_radius,
	});
}

export function peachpay_isProductPage() {
	const isPageWithSingleProduct = ($qs('.single_add_to_cart_button') !== null) || ($qsAll('[data-block-name="woocommerce/featured-product"]').length === 1);
	return !peachpay_data.is_cart_page && !peachpay_data.is_category_page && (peachpay_data.is_product || isPageWithSingleProduct);
}

export function peachpay_placeButtonMiniCart() {
	if (!shouldLoadButtonBasedOnAccountSettings()) {
		return;
	}

	const miniCartButtons = $qs('.woocommerce-mini-cart__buttons')
		?? $qs('.xoo-wsc-footer')
		?? $qs('.orderable-mini-cart__buttons');

	if (!miniCartButtons) {
		requestAnimationFrame(peachpay_placeButtonMiniCart);
		return;
	}

	const miniCart = $qs('#pp-button-mini');

	// Avoid placing mini-cart twice
	if (miniCart) {
		return;
	}

	miniCartButtons.insertAdjacentHTML('beforeend', peachpayMiniButtonTemplate());

	if (miniCartButtons.querySelector('#payment-methods-container-minicart') && !peachpay_data.button_display_payment_method_icons) {
		miniCartButtons.querySelector('#payment-methods-container-minicart')?.classList.add('hide');
	}

	if (miniCartButtons.querySelector('#button-icon-minicart')) {
		update_buttonIcon(peachpay_data.button_icon, 'minicart');
	}

	adjustMiniButtonPerSite();

	peachpay_initButton({
		alignment: peachpay_data.button_alignment_product_page,
		isMiniCart: true,
	});
}

// Specific adjustments for a more native look
function adjustMiniButtonPerSite() {
	const miniButton = $qs<HTMLButtonElement>('#pp-button-mini');

	if (miniButton && location.hostname === 'salafibookstore.com') {
		miniButton.style.padding = '18px';
	}
}

/**
 * This is here and not in the Elementor JS file because the Elementor JS file
 * is not loaded if Elementor is not active.
 */
export function peachpay_isElementor() {
	return $qs('.elementor-pp-button');
}

/**
 * We don't want to display express checkout button on affiliate/external products.
 */
function peachpay_isAffiliateProduct() {
	return peachpay_data.product_type === 'external' && !peachpay_data.is_shop_page;
}
